import { useCallback, useEffect, useRef, useState } from 'react';
import BookingSocket from '../services/wsService';
import apiService from '../services/api';

/**
 * Manages a booking's real-time channel for a component.
 *
 * - Opens a WebSocket for chat + live location (auto-reconnecting).
 * - Tracks the live tracking on/off state (server-driven) and the handler's
 *   latest position.
 * - For the handler, while sharing is on, streams device GPS over the socket.
 *
 * @param {number} bookingId
 * @param {Object} opts - { enabled, isHandler }
 */
export function useBookingSocket(bookingId, { enabled = true, isHandler = false } = {}) {
  const [connectionState, setConnectionState] = useState('disconnected');
  const [messages, setMessages] = useState([]);
  const [trackingActive, setTrackingActive] = useState(false);
  const [handlerLocation, setHandlerLocation] = useState(null);
  const socketRef = useRef(null);
  const geoWatchRef = useRef(null);

  // Open / close the socket with the booking.
  useEffect(() => {
    if (!enabled || !bookingId) return undefined;

    const socket = new BookingSocket(bookingId, {
      onConnectionState: setConnectionState,
      onMessage: (data) => setMessages((prev) => [...prev, data]),
      onLocation: (data) =>
        setHandlerLocation({
          latitude: Number(data.latitude),
          longitude: Number(data.longitude),
          timestamp: data.timestamp,
        }),
      onTracking: (data) => {
        setTrackingActive(!!data.active);
        if (data.last_location) {
          setHandlerLocation({
            latitude: Number(data.last_location.latitude),
            longitude: Number(data.last_location.longitude),
            timestamp: data.last_location.timestamp,
          });
        }
      },
    });
    socketRef.current = socket;
    socket.connect();

    return () => {
      socket.disconnect();
      socketRef.current = null;
    };
  }, [bookingId, enabled]);

  // Handler only: while sharing is on, stream GPS over the socket.
  useEffect(() => {
    if (!isHandler || !trackingActive) return undefined;
    if (!('geolocation' in navigator)) return undefined;

    const watchId = navigator.geolocation.watchPosition(
      (pos) => {
        socketRef.current?.sendLocation(
          pos.coords.latitude,
          pos.coords.longitude,
        );
      },
      (err) => console.warn('Geolocation error:', err.message),
      { enableHighAccuracy: true, maximumAge: 5000, timeout: 20000 },
    );
    geoWatchRef.current = watchId;

    return () => {
      navigator.geolocation.clearWatch(watchId);
      geoWatchRef.current = null;
    };
  }, [isHandler, trackingActive]);

  const sendChat = useCallback((message) => {
    // Prefer the socket; fall back to REST so the message still sends offline.
    const sent = socketRef.current?.sendChat(message);
    if (!sent) {
      return apiService.request(`/services/bookings/${bookingId}/updates/`, {
        method: 'POST',
        body: JSON.stringify({ update_type: 'text', message }),
      });
    }
    return Promise.resolve();
  }, [bookingId]);

  // Toggle sharing via REST (the source of truth); the server broadcasts the
  // new state back over the socket, which flips `trackingActive`.
  const setSharing = useCallback(async (active) => {
    await apiService.request(`/services/bookings/${bookingId}/tracking/`, {
      method: 'POST',
      body: JSON.stringify({ active }),
    });
  }, [bookingId]);

  return {
    connectionState,
    messages,
    trackingActive,
    handlerLocation,
    sendChat,
    setSharing,
  };
}

export default useBookingSocket;
